import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Phone, MapPin, Mail, Star } from "lucide-react";

import Seo from "@/components/site/Seo";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ChatPlaceholder from "@/components/site/ChatPlaceholder";
import Marquee from "@/components/site/Marquee";
import Faq from "@/components/site/Faq";
import ContactForm from "@/components/site/ContactForm";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { WhatsAppIcon } from "@/components/site/icons";
import { WingDivider, Wings } from "@/components/site/Wings";
import MapEmbed from "@/components/site/MapEmbed";

import { services, stats, heroTrust, phases, creds, faqItems, hopeMessages } from "@/lib/content";
import { clinicSchema, faqSchema } from "@/lib/schema";
import { waLink, PHONE_DISPLAY, PHONE_TEL, EMAIL, ADDRESS } from "@/lib/contact";

export default function Home() {
  const reduce = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.08]);

  return (
    <div className="relative">
      <Seo
        title="Clínica Especialista en Salud Mental y Adicciones en Cuernavaca | CESMA"
        description="CESMA es una clínica especialista en salud mental y adicciones en Cuernavaca, Morelos. Tratamiento residencial interdisciplinario, confidencial y humanista, con más de 27 años de experiencia. Sucursales para hombres y mujeres."
        canonical="https://www.cesma.com.mx/"
        jsonLd={[clinicSchema, faqSchema]}
      />
      <div className="grain" />
      <Header />

      <main id="inicio">
        {/* HERO — banner inmersivo (todas las resoluciones) */}
        <section ref={heroRef} className="relative flex min-h-[92vh] items-center overflow-hidden">
          <div className="absolute inset-0 z-0" aria-hidden="true">
            <motion.img
              style={{ scale: imgScale }}
              src="/assets/jardin.webp"
              alt=""
              fetchPriority="high"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-pine/90 via-pine/70 to-pine/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-pine/70 via-transparent to-transparent" />
          </div>

          <div className="relative z-10 mx-auto w-full max-w-7xl px-5 py-20 md:px-8 md:py-28">
            <div className="max-w-2xl">
              <motion.div
                initial={{ opacity: 0, y: reduce ? 0 : 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-[0.8rem] font-semibold text-white backdrop-blur-sm"
              >
                <span className="h-2 w-2 rounded-full bg-marigold" /> Estamos contigo · Atención 7 días
              </motion.div>
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="eyebrow block text-white/85"
              >
                Cuernavaca, Morelos · Desde 1998
              </motion.span>
              <h1 className="mt-4 font-serif text-[2.5rem] leading-[1.08] text-white [text-shadow:0_1px_22px_rgba(0,0,0,.32)] sm:text-5xl lg:text-[3.7rem]">
                <MaskedLines
                  lines={[
                    "Clínica especialista en",
                    "salud mental y adicciones",
                    <>
                      en Cuernavaca, con <em className="italic text-marigold">dignidad</em>
                    </>,
                    "y acompañamiento real.",
                  ]}
                />
              </h1>
              <Reveal delay={0.55} className="mt-6 max-w-[44ch] text-lg text-white/90">
                Un tratamiento residencial, confidencial e interdisciplinario para que tu
                familiar recupere su equilibrio y su sentido de vida.
              </Reveal>

              <Reveal delay={0.65} className="mt-8 flex flex-wrap gap-3">
                <motion.a
                  whileHover={reduce ? {} : { y: -2 }}
                  href={waLink("Hola, necesito orientación sobre un familiar.")}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-marigold px-7 py-3.5 font-semibold text-[#231402] shadow-soft transition-shadow duration-300 hover:shadow-lift"
                  data-testid="hero-whatsapp"
                >
                  <WhatsAppIcon className="h-[1.15em] w-[1.15em]" />
                  Hablar con un especialista
                </motion.a>
                <motion.a
                  whileHover={reduce ? {} : { y: -2 }}
                  href={`tel:${PHONE_TEL}`}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border-[1.5px] border-white px-7 py-3.5 font-semibold text-white transition-colors duration-300 hover:bg-white hover:text-pine"
                  data-testid="hero-phone"
                >
                  <Phone className="h-[1.1em] w-[1.1em]" strokeWidth={1.8} />
                  {PHONE_DISPLAY}
                </motion.a>
              </Reveal>

              <Reveal delay={0.72} className="mt-4 text-[0.92rem] text-white/85">
                Te escuchamos ahora mismo, de forma confidencial y sin compromiso.
              </Reveal>

              <Reveal delay={0.8} className="mt-10 flex flex-wrap gap-x-9 gap-y-5">
                {heroTrust.map((t) => (
                  <div key={t.label} className="flex flex-col">
                    <b className="font-serif text-3xl text-white">{t.value}</b>
                    <span className="mt-1 max-w-[15ch] text-[0.8rem] text-white/80">{t.label}</span>
                  </div>
                ))}
              </Reveal>
            </div>
          </div>
        </section>

        {/* MARQUEE (slow, calm) */}
        <div className="border-y border-line/70 bg-warm/60 py-6">
          <Marquee
            items={[
              "Dignidad",
              "Confidencialidad",
              "Acompañamiento",
              "Humanismo",
              "Esperanza",
              "Familia",
            ]}
          />
        </div>

        {/* SERVICIOS */}
        <section id="ayuda" className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Reveal className="max-w-2xl">
            <span className="eyebrow">Cómo ayudamos</span>
            <h2 className="mt-3 font-serif text-[1.9rem] text-ink sm:text-4xl">
              Un solo lugar para la salud mental y las adicciones
            </h2>
            <p className="mt-4 text-[1.08rem] text-soft">
              Atendemos a la persona completa, no solo el síntoma. Nuestro equipo integra
              psiquiatría, psicología, medicina y trabajo social bajo un mismo techo.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => (
              <Reveal
                as="article"
                key={s.title}
                delay={(i % 3) * 0.08}
                whileHover={reduce ? {} : { y: -4 }}
                className="group rounded-2xl border border-line bg-warm p-8 shadow-soft transition-shadow duration-300 hover:shadow-lift"
                data-testid={`service-card-${i}`}
              >
                <div className="mb-5 grid h-12 w-12 place-items-center rounded-xl bg-spring/12 text-pine transition-colors duration-300 group-hover:bg-magenta/12 group-hover:text-magentaDeep">
                  <s.Icon strokeWidth={1.5} />
                </div>
                <h3 className="mb-2 font-serif text-xl text-ink">{s.title}</h3>
                <p className="text-[0.98rem] text-soft">{s.text}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* STATS */}
        <section className="border-y border-line bg-warm py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-10 px-5 text-center md:grid-cols-4 md:px-8">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08} className="md:border-l md:border-line md:first:border-l-0">
                <b className="block font-serif text-[2.6rem] leading-none text-pine md:text-[3rem]">
                  {s.value}
                </b>
                <span className="mt-3 block text-[0.92rem] text-soft">{s.label}</span>
              </Reveal>
            ))}
          </div>
        </section>

        {/* MODELO — numbered chapters */}
        <section id="modelo" className="relative overflow-hidden bg-pine text-[#EAF0E7]">
          <img
            src="/assets/logo-mark.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -left-20 top-1/2 w-[420px] -translate-y-1/2 opacity-[0.06]"
          />
          <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 py-20 md:px-8 md:py-28 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
            <Reveal>
              <span className="eyebrow" style={{ color: "#9BC3A5" }}>
                Modelo interdisciplinario
              </span>
              <h2 className="mt-3 font-serif text-[1.9rem] text-white sm:text-4xl">
                Un tratamiento hecho a la medida de cada persona
              </h2>
              <p className="mt-4 text-[#C3D2C4]">
                No hay dos historias iguales. Diseñamos un plan individual a partir de una
                evaluación completa, y lo ajustamos conforme la persona avanza.
              </p>
              <p className="mt-5 text-[#C3D2C4]">Contamos con dos planes residenciales:</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {["Plan de 45 días", "Plan de 90 días"].map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.07] px-4 py-2 text-[0.9rem] font-semibold"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </Reveal>

            <div className="grid gap-0.5 overflow-hidden rounded-2xl">
              {phases.map((p, i) => (
                <Reveal
                  key={p.n}
                  delay={i * 0.1}
                  className="grid grid-cols-[auto_1fr] gap-5 bg-white/[0.05] px-6 py-6 transition-colors duration-300 hover:bg-white/[0.09] md:px-7"
                  data-testid={`phase-${p.n}`}
                >
                  <span className="font-serif text-2xl font-semibold text-marigold">{p.n}</span>
                  <div>
                    <h4 className="mb-1 font-sans text-[1.06rem] font-semibold text-white">
                      {p.title}
                    </h4>
                    <p className="text-[0.94rem] text-[#B9C9BB]">{p.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* FAMILIA */}
        <section id="familia" className="bg-gradient-to-b from-plaster to-band">
          <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 py-20 md:px-8 md:py-28 lg:grid-cols-2 lg:gap-16">
            <Reveal className="order-2 lg:order-1">
              <div className="overflow-hidden rounded-[26px] shadow-lift">
                <img
                  src="/assets/terraza.webp"
                  alt="Terraza de descanso en las instalaciones de CESMA, pensada para la convivencia"
                  width="1600"
                  height="1066"
                  className="aspect-[5/4] w-full object-cover transition-transform duration-700 hover:scale-[1.04]"
                />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="order-1 lg:order-2">
              <span className="eyebrow">Acompañamiento a la familia</span>
              <h2 className="mt-3 font-serif text-[1.9rem] text-ink sm:text-4xl">
                La recuperación se sostiene en casa
              </h2>
              <p className="mt-4 text-soft">
                La familia es parte de la solución. Cuando participa activamente del proceso,
                la recuperación avanza en la misma dirección y con más fuerza.
              </p>
              <p className="my-3">
                <span className="font-serif text-[2.4rem] text-magenta">hasta 60%</span>
              </p>
              <p className="text-soft">
                puede aportar el acompañamiento familiar a la recuperación cuando forma parte
                del tratamiento. Por eso ofrecemos{" "}
                <b className="text-ink">terapias grupales sabatinas</b> y{" "}
                <b className="text-ink">terapia familiar individual</b>, para que nadie camine solo.
              </p>
            </Reveal>
          </div>
        </section>

        {/* MOMENTO DE CALMA — banda atmosférica */}
        <section aria-hidden="true" className="photo-frame relative h-[38vh] min-h-[280px] overflow-hidden">
          <img src="/assets/atmos-courtyard.webp" alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-pine/55 via-pine/10 to-transparent" />
          <div className="absolute inset-0 grid place-items-center">
            <Wings className="h-12 w-48 text-warm/90" />
          </div>
        </section>

        {/* INSTALACIONES — spotlight gallery */}
        <section id="instalaciones" className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Reveal className="max-w-2xl">
            <span className="eyebrow">Instalaciones</span>
            <h2 className="mt-3 font-serif text-[1.9rem] text-ink sm:text-4xl">
              Para estar bien por dentro, hay que estar bien por fuera
            </h2>
            <p className="mt-4 text-[1.08rem] text-soft">
              Ubicados en el fraccionamiento Burgos, una de las zonas más tranquilas de
              Morelos, con instalaciones pensadas para que tu familiar se sienta como en casa.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3 md:grid-rows-2 md:[height:520px]">
            {[
              { src: "/assets/jardin.webp", cap: "Áreas verdes", alt: "Jardines con buganvilias y áreas verdes en CESMA Cuernavaca", big: true },
              { src: "/assets/terraza.webp", cap: "Áreas de descanso", alt: "Terraza de descanso con mobiliario en las instalaciones de CESMA" },
              { src: "/assets/habitacion.webp", cap: "Habitaciones", alt: "Habitación privada con vista a los jardines en CESMA" },
            ].map((g, i) => (
              <Reveal
                key={g.cap}
                delay={i * 0.1}
                className={`group relative overflow-hidden rounded-2xl shadow-soft ${
                  g.big ? "md:col-span-2 md:row-span-2" : ""
                }`}
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  width="1600"
                  height="1066"
                  className="h-64 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 md:h-full"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-pine/55 via-transparent to-transparent" />
                <figcaption className="absolute bottom-3 left-4 font-semibold text-white drop-shadow">
                  {g.cap}
                </figcaption>
              </Reveal>
            ))}
          </div>
        </section>

        {/* CONFIANZA */}
        <section id="confianza" className="mx-auto max-w-7xl px-5 pb-20 md:px-8 md:pb-28">
          <Reveal className="rounded-[28px] border border-line bg-warm p-8 shadow-soft md:p-12">
            <div className="max-w-2xl">
              <span className="eyebrow">Registros y certificaciones</span>
              <h2 className="mt-3 font-serif text-[1.9rem] text-ink sm:text-4xl">
                Una clínica legalmente reconocida
              </h2>
              <p className="mt-4 text-soft">
                Operamos con los avisos y registros sanitarios que respaldan un tratamiento
                serio y responsable.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {creds.map((c, i) => (
                <Reveal
                  key={c.name}
                  delay={i * 0.08}
                  className="rounded-2xl border border-line bg-band p-6"
                  data-testid={`cred-${i}`}
                >
                  <b className="mb-1 block font-serif text-[1.05rem] text-pine">{c.name}</b>
                  <span className="text-[0.85rem] text-soft">{c.text}</span>
                </Reveal>
              ))}
            </div>
            <p className="mt-6 text-[0.9rem] text-soft">
              Operado por <b className="text-pine">Salud Integral Alas Enlazadas A.C.</b> ·
              Contamos con becas y facilidades de pago.
            </p>
          </Reveal>
        </section>

        {/* TESTIMONIO */}
        <section className="border-y border-line bg-warm py-20 md:py-24">
          <Reveal className="mx-auto max-w-3xl px-5 text-center md:px-8">
            <WingDivider className="mb-8" />
            <div className="mb-3 flex justify-center gap-1 text-marigold" aria-label="5 de 5 estrellas">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-current" strokeWidth={0} />
              ))}
            </div>
            <blockquote className="font-serif text-[1.4rem] italic leading-[1.4] text-ink sm:text-[2rem]">
              “Llevo ocho meses sin consumir. Lo que más me ayudó fueron los grupos de ayuda
              mutua y el trato: existe una diferencia enorme respecto a otras clínicas por las
              que había pasado.”
            </blockquote>
            <cite className="mt-4 block text-[0.95rem] not-italic text-soft">
              — Testimonio de un paciente de CESMA
            </cite>
          </Reveal>
        </section>

        {/* HISTORIAS DE ESPERANZA */}
        <section className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Reveal className="mx-auto mb-12 max-w-2xl text-center">
            <span className="eyebrow">Historias de esperanza</span>
            <h2 className="mt-3 font-serif text-[1.9rem] text-ink sm:text-4xl">
              Voces de familias que dieron el paso
            </h2>
            <p className="mx-auto mt-4 max-w-prose text-soft">
              Mensajes anónimos que familias han querido compartir sobre su proceso de
              acompañamiento en CESMA.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {hopeMessages.map((m, i) => (
              <Reveal
                key={i}
                delay={(i % 2) * 0.1}
                whileHover={reduce ? {} : { y: -4 }}
                className="relative rounded-2xl border border-line bg-warm p-8 shadow-soft transition-shadow duration-300 hover:shadow-lift"
                data-testid={`hope-${i}`}
              >
                <span aria-hidden="true" className="absolute right-6 top-3 font-serif text-6xl leading-none text-magenta/20">”</span>
                <p className="relative font-serif text-[1.2rem] italic leading-[1.45] text-ink">
                  {m.text}
                </p>
                <div className="mt-5 flex items-center gap-3">
                  <Wings className="h-4 w-16 text-magenta/60" />
                  <cite className="text-[0.9rem] not-italic font-semibold text-soft">— {m.who}</cite>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mx-auto mt-8 max-w-prose text-center text-[0.8rem] text-soft/80">
            Mensajes representativos compartidos de forma anónima, con el consentimiento de las
            familias, para cuidar su privacidad.
          </p>
        </section>

        {/* FAQ */}
        <section id="faq" className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Reveal className="mx-auto mb-12 max-w-2xl text-center">
            <span className="eyebrow">Preguntas frecuentes</span>
            <h2 className="mt-3 font-serif text-[1.9rem] text-ink sm:text-4xl">
              Resolvemos tus dudas
            </h2>
            <p className="mx-auto mt-4 max-w-prose text-soft">
              Estamos aquí para ayudarte. Si tu pregunta no está aquí, escríbenos por WhatsApp.
            </p>
          </Reveal>
          <Faq items={faqItems} />
        </section>

        {/* CONTACTO */}
        <section id="contacto" className="bg-gradient-to-b from-plaster to-band">
          <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-12 px-5 py-20 md:px-8 md:py-28 lg:grid-cols-2">
            <Reveal>
              <span className="eyebrow">Estamos para escucharte</span>
              <h2 className="mt-3 font-serif text-[1.9rem] text-ink sm:text-4xl">
                Dar el primer paso es lo más difícil
              </h2>
              <p className="mt-4 text-soft">
                Escríbenos o llámanos. Con una llamada podemos orientarte sobre el plan ideal
                para ti o tu familia, de forma confidencial y sin compromiso.
              </p>
              <ul className="mt-8 grid gap-5">
                <li className="flex items-start gap-3">
                  <Phone className="mt-0.5 h-[22px] w-[22px] flex-none text-spring" strokeWidth={1.7} />
                  <span>
                    <b className="block">Teléfono y WhatsApp</b>
                    <a href={`tel:${PHONE_TEL}`} className="text-ink hover:text-spring" data-testid="contact-phone">
                      {PHONE_DISPLAY}
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="mt-0.5 h-[22px] w-[22px] flex-none text-spring" strokeWidth={1.7} />
                  <span>
                    <b className="block">Correo</b>
                    <a href={`mailto:${EMAIL}`} className="text-ink hover:text-spring" data-testid="contact-email">
                      {EMAIL}
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-[22px] w-[22px] flex-none text-spring" strokeWidth={1.7} />
                  <span>
                    <b className="block">Ubicación</b>
                    {ADDRESS}
                  </span>
                </li>
              </ul>
              <MapEmbed className="mt-8" />
            </Reveal>
            <Reveal delay={0.1}>
              <ContactForm />
            </Reveal>
          </div>
        </section>
      </main>

      <Footer />
      <ChatPlaceholder />
    </div>
  );
}
